/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCalendarRange, faCheck, faSortDown } from '@fortawesome/pro-solid-svg-icons';
import * as dateFns from 'date-fns';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';

export enum TaskDateFilterPresets {
  lastSevenDays,
  lastWeek,
  thisWeek,
  nextWeek,
  lastMonth,
  thisMonth,
  nextMonth,
  custom
}

export enum LayoutDirection {
  vertical,
  horizontal
}

export interface DateFilterValue {
  startDate: Date;
  endDate: Date;
  preset: TaskDateFilterPresets;
}

@Component({
  selector: 'app-date-range-picker',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    FontAwesomeModule,
    MatSlideToggleModule,
    NgxMaterialTimepickerModule
  ],
  templateUrl: './date-range-picker.component.html',
  styleUrl: './date-range-picker.component.scss'
})
export class DateRangePickerComponent implements OnChanges {
  @Input() dateFilterValue!: DateFilterValue;
  @Input() collapsible: boolean = false;

  get currentPreset(): TaskDateFilterPresets {
    return this.dateFilterValue?.preset ?? TaskDateFilterPresets.lastSevenDays;
  }
  @Input() withTime: boolean = false; // Currently only supported with [separateStartEnd]=true
  @Input() separateStartEnd: boolean = false;
  @Input() layoutDirection: LayoutDirection = LayoutDirection.horizontal;

  @Output() dateFilterChange = new EventEmitter<DateFilterValue>();

  open: boolean = false;

  // FontAwesome Icons
  readonly faCalendarRange = faCalendarRange;
  readonly faSortDown = faSortDown;
  readonly faCheck = faCheck;

  readonly TaskDateFilterPresets = TaskDateFilterPresets;
  readonly LayoutDirection = LayoutDirection;

  taskDateFilter: FormGroup;

  constructor() {
    const defaultDates = DateRangePickerComponent.computeDatesForPreset(
      TaskDateFilterPresets.lastSevenDays
    )!;
    this.taskDateFilter = new FormGroup({
      startDate: new FormControl(defaultDates.startDate),
      endDate: new FormControl(defaultDates.endDate),
      startTime: new FormControl('00:00'),
      endTime: new FormControl('23:59')
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dateFilterValue) {
      const val = changes.dateFilterValue.currentValue;
      if (val) {
        if (changes.dateFilterValue.firstChange) {
          this.open = val.preset === TaskDateFilterPresets.custom;
        }

        if (val.preset !== TaskDateFilterPresets.custom) {
          const dates = DateRangePickerComponent.computeDatesForPreset(val.preset);
          if (dates) {
            this.taskDateFilter.controls.startDate.setValue(dates.startDate);
            this.taskDateFilter.controls.endDate.setValue(dates.endDate);
            this.taskDateFilter.controls.startTime.setValue('00:00');
            this.taskDateFilter.controls.endTime.setValue('23:59');
          }
        } else {
          this.taskDateFilter.controls.startDate.setValue(val.startDate);
          this.taskDateFilter.controls.endDate.setValue(val.endDate);
          if (this.withTime) {
            this.taskDateFilter.controls.startTime.setValue(
              `${String(val.startDate.getHours()).padStart(2, '0')}:${String(val.startDate.getMinutes()).padStart(2, '0')}`
            );
            this.taskDateFilter.controls.endTime.setValue(
              `${String(val.endDate.getHours()).padStart(2, '0')}:${String(val.endDate.getMinutes()).padStart(2, '0')}`
            );
          } else {
            this.taskDateFilter.controls.startTime.setValue('00:00');
            this.taskDateFilter.controls.endTime.setValue('23:59');
          }
        }
      }
    }
  }

  onTaskDateFilterPresetClick(preset: TaskDateFilterPresets): void {
    this.applyDateFilterPreset(preset);
  }

  applyDateFilterPreset(preset: TaskDateFilterPresets): void {
    const dates = DateRangePickerComponent.computeDatesForPreset(preset);

    if (dates) {
      this.taskDateFilter.controls.startDate.setValue(dates.startDate);
      this.taskDateFilter.controls.endDate.setValue(dates.endDate);

      this.taskDateFilter.controls.startTime.setValue('00:00');
      this.taskDateFilter.controls.endTime.setValue('23:59');
    }

    this.emitDateFilterChange(preset);
  }

  onCustomTaskDateFilterPicked(): void {
    const hasDates = this.separateStartEnd
      ? !!(
          this.taskDateFilter.controls.startDate.value && this.taskDateFilter.controls.endDate.value
        )
      : !!this.taskDateFilter.controls.endDate.value;
    const hasTimes =
      !this.withTime ||
      !!(
        this.taskDateFilter.controls.startTime.value && this.taskDateFilter.controls.endTime.value
      );

    if (hasDates && hasTimes) {
      this.emitDateFilterChange(TaskDateFilterPresets.custom);
    }
  }

  getTaskDateFilterPresetsLabel(preset: TaskDateFilterPresets): string {
    switch (preset) {
      case TaskDateFilterPresets.lastSevenDays:
        return $localize`:@@lastSevenDays:Last 7 Days`;
      case TaskDateFilterPresets.lastWeek:
        return $localize`:@@previousWeek:Previous week`;
      case TaskDateFilterPresets.thisWeek:
        return $localize`:@@thisWeek:This week`;
      case TaskDateFilterPresets.nextWeek:
        return $localize`:@@nextWeek:Next week`;
      case TaskDateFilterPresets.lastMonth:
        return $localize`:@@previousMonth:Previous month`;
      case TaskDateFilterPresets.thisMonth:
        return $localize`:@@thisMonth:This month`;
      case TaskDateFilterPresets.nextMonth:
        return $localize`:@@nextMonth:Next month`;
      case TaskDateFilterPresets.custom:
        return $localize`:@@custom:Custom`;
    }
  }

  static formatDateFilterParam(value: DateFilterValue, withTime: boolean): string {
    if (withTime) {
      return `${formatDate(value.startDate, "yyyy-MM-ddTHH:mm'Z'", 'en-US', 'UTC')}_${formatDate(value.endDate, "yyyy-MM-ddTHH:mm'Z'", 'en-US', 'UTC')}`;
    } else {
      return `${formatDate(value.startDate, 'yyyy-MM-dd', 'en-US')}_${formatDate(value.endDate, 'yyyy-MM-dd', 'en-US')}`;
    }
  }

  static parseDateFilterParam(param: string, withTime: boolean): DateFilterValue | null {
    const regex = withTime
      ? new RegExp(String.raw`\d{4}-\d{2}-\d{2}T\d{2}:\d{2}Z_\d{4}-\d{2}-\d{2}T\d{2}:\d{2}Z`)
      : new RegExp(String.raw`\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}`);

    if (!param || !regex.test(param)) {
      return null;
    }

    const parts = param.split('_');

    const startDate = withTime
      ? dateFns.parseISO(parts[0])
      : dateFns.startOfDay(dateFns.parse(parts[0], 'yyyy-MM-dd', new Date()));
    const endDate = withTime
      ? dateFns.parseISO(parts[1])
      : dateFns.endOfDay(dateFns.parse(parts[1], 'yyyy-MM-dd', new Date()));

    if (!dateFns.isValid(startDate) || !dateFns.isValid(endDate) || startDate > endDate) {
      return null;
    }

    return {
      startDate,
      endDate,
      preset: TaskDateFilterPresets.custom
    };
  }

  private static readonly presetParamMap: Record<string, TaskDateFilterPresets> = {
    lastSevenDays: TaskDateFilterPresets.lastSevenDays,
    lastWeek: TaskDateFilterPresets.lastWeek,
    thisWeek: TaskDateFilterPresets.thisWeek,
    nextWeek: TaskDateFilterPresets.nextWeek,
    lastMonth: TaskDateFilterPresets.lastMonth,
    thisMonth: TaskDateFilterPresets.thisMonth,
    nextMonth: TaskDateFilterPresets.nextMonth,
    custom: TaskDateFilterPresets.custom
  };

  static formatPresetParam(preset: TaskDateFilterPresets): string {
    const entry = Object.entries(DateRangePickerComponent.presetParamMap).find(
      ([, v]) => v === preset
    );
    return entry ? entry[0] : 'lastSevenDays';
  }

  static parsePresetParam(param: string): TaskDateFilterPresets | null {
    return DateRangePickerComponent.presetParamMap[param] ?? null;
  }

  static computeDatesForPreset(
    preset: TaskDateFilterPresets,
    now: Date = new Date()
  ): { startDate: Date; endDate: Date } | null {
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    switch (preset) {
      case TaskDateFilterPresets.lastSevenDays:
        startDate = dateFns.subDays(now, 7);
        endDate = new Date(now);
        break;
      case TaskDateFilterPresets.lastWeek:
        endDate = dateFns.subDays(dateFns.startOfISOWeek(now), 1);
        startDate = dateFns.startOfISOWeek(endDate);
        break;
      case TaskDateFilterPresets.thisWeek:
        startDate = dateFns.startOfISOWeek(now);
        endDate = dateFns.endOfISOWeek(now);
        break;
      case TaskDateFilterPresets.nextWeek:
        startDate = dateFns.addDays(dateFns.endOfISOWeek(now), 1);
        endDate = dateFns.endOfISOWeek(startDate);
        break;
      case TaskDateFilterPresets.lastMonth:
        endDate = dateFns.subDays(dateFns.startOfMonth(now), 1);
        startDate = dateFns.startOfMonth(endDate);
        break;
      case TaskDateFilterPresets.thisMonth:
        startDate = dateFns.startOfMonth(now);
        endDate = dateFns.endOfMonth(now);
        break;
      case TaskDateFilterPresets.nextMonth:
        startDate = dateFns.addDays(dateFns.endOfMonth(now), 1);
        endDate = dateFns.endOfMonth(startDate);
        break;
      case TaskDateFilterPresets.custom:
      default:
        return null;
    }

    return startDate && endDate
      ? { startDate: dateFns.startOfDay(startDate), endDate: dateFns.endOfDay(endDate) }
      : null;
  }

  private combineDateAndTime(date: Date, time: string): Date {
    const [hours, minutes] = time.split(':').map(Number);
    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);
    return combined;
  }

  private emitDateFilterChange(preset?: TaskDateFilterPresets): void {
    let startDate: Date = this.taskDateFilter.controls.startDate.value;
    let endDate: Date = this.taskDateFilter.controls.endDate.value;

    if (this.withTime) {
      startDate = this.combineDateAndTime(startDate, this.taskDateFilter.controls.startTime.value);
      endDate = this.combineDateAndTime(endDate, this.taskDateFilter.controls.endTime.value);
    }

    this.dateFilterChange.emit({
      startDate,
      endDate,
      preset: preset ?? this.currentPreset
    });
  }
}
